import React, { useState } from 'react';
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonIcon,
  IonText,
  IonList,
  IonItem,
  IonLabel,
} from '@ionic/react';
import { arrowBack, attachOutline, cloudDownloadOutline } from 'ionicons/icons';
import { ReportAttachment, reportingService } from '@/services/ReportingService';
import ModalFooter from '@/components/common/ModalFooter';

interface Props {
  isOpen: boolean;
  attachments: ReportAttachment[];
  onClose: () => void;
}

const AttachmentConfirmModal: React.FC<Props> = ({ isOpen, attachments, onClose }) => {
  const [zipFilename, setZipFilename] = useState('attachments');
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    setDownloading(true);
    setError(null);
    try {
      await reportingService.downloadAttachmentsAsZip(attachments, zipFilename.trim() || 'attachments');
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create zip file.');
    } finally {
      setDownloading(false);
    }
  };

  const handleClose = () => {
    if (!downloading) {
      setError(null);
      onClose();
    }
  };

  return (
    <IonModal isOpen={isOpen} onDidDismiss={handleClose} style={{ '--width': '480px', '--height': 'auto', '--max-height': '70vh' }}>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Download Attachments</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <div style={{ padding: '16px' }}>
          {attachments.length === 0 ? (
            <IonText color="medium">
              <p style={{ textAlign: 'center', fontStyle: 'italic' }}>
                No attachments found in the selected rows.
              </p>
            </IonText>
          ) : (
            <>
              <IonText color="medium">
                <p style={{ marginBottom: '12px', fontSize: '0.9rem' }}>
                  The following {attachments.length} attachment{attachments.length !== 1 ? 's' : ''} will be archived into a zip file:
                </p>
              </IonText>
              <IonList style={{ borderRadius: '8px', overflow: 'hidden', border: '1px solid var(--ion-color-light-shade)', marginBottom: '16px' }}>
                {attachments.map((att, idx) => (
                  <IonItem key={`${att.attachmentId}-${idx}`} lines={idx < attachments.length - 1 ? 'inset' : 'none'}>
                    <IonIcon icon={attachOutline} slot="start" color="medium" style={{ fontSize: '18px' }} />
                    <IonLabel>
                      <div style={{ fontWeight: '500', fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {att.filename}
                      </div>
                      {att.description && att.description !== att.filename && (
                        <div style={{ fontSize: '0.75rem', color: 'var(--ion-color-medium)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {att.description}
                        </div>
                      )}
                    </IonLabel>
                  </IonItem>
                ))}
              </IonList>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <IonText style={{ fontSize: '0.85rem', color: '#666', whiteSpace: 'nowrap' }}>Zip filename:</IonText>
                <input
                  value={zipFilename}
                  onChange={(e) => setZipFilename(e.target.value)}
                  disabled={downloading}
                  style={{
                    flex: 1,
                    border: '1px solid var(--ion-color-light-shade)',
                    borderRadius: '4px',
                    padding: '4px 8px',
                    fontSize: '0.9rem',
                    color: 'var(--ion-text-color)',
                    background: 'var(--ion-background-color)',
                  }}
                />
                <IonText style={{ fontSize: '0.85rem', color: '#888' }}>.zip</IonText>
              </div>

              {error && (
                <IonText color="danger" style={{ fontSize: '0.85rem', marginTop: '8px', display: 'block' }}>
                  ⚠ {error}
                </IonText>
              )}
            </>
          )}
        </div>
      </IonContent>

      <ModalFooter
        cancelLabel="Cancel"
        actionLabel={downloading ? 'Zipping...' : 'Download as Zip'}
        cancelIcon={arrowBack}
        actionIcon={cloudDownloadOutline}
        onCancel={handleClose}
        onAction={handleConfirm}
        isCancelDisabled={downloading}
        isActionDisabled={attachments.length === 0 || downloading}
        isActionLoading={downloading}
        cancelFill="outline"
      />
    </IonModal>
  );
};

export default AttachmentConfirmModal;
